import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';

/**
 * Visual intent/severity options
 */
export type ButtonIntent = 'primary' | 'brand' | 'danger' | 'warning' | 'success' | 'default';

/**
 * Appearance options
 */
export type ButtonAppearance = 'default' | 'outlined' | 'minimal';

/**
 * Size options
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonSignature {
  Args: {
    /**
     * Stable identity for automation: emitted as `data-test-hook` on this
     * component's root, alongside its `data-test-*` kind markers.
     *
     * The call site says WHICH instance this is; the component already says
     * what KIND it is. Together they give a key that survives a re-render,
     * which `id` does not — 74% of ids in these apps are Ember-generated
     * (`…-ember42`) and change whenever the component tree is rebuilt. One
     * hook covers the whole subtree: a descendant control is identified as
     * its nearest ancestor hook plus its own role.
     *
     * Consumed by the ux-audit's per-element interaction ledger to tell "a
     * change broke this control" from "the page re-rendered in a different
     * order". See tuitionio/tio-front-end#10057.
     *
     * @example <Button @testHook="tas-approval-queue__approve">Approve</Button>
     */
    testHook?: string;
    /** HTML button type */
    type?: 'button' | 'submit' | 'reset';
    /** Visual intent */
    intent?: ButtonIntent;
    /** Appearance variant */
    appearance?: ButtonAppearance;
    /** Size */
    size?: ButtonSize;
    /** Show loading state (disables button, shows loading indicator) */
    isRunning?: boolean;
    /** Disabled state */
    isDisabled?: boolean;
    /** Icon component (Phosphor icon etc.) */
    icon?: ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;
    /** Icon size override */
    iconSize?: number;
    /** Text label */
    label?: string;
    /** Accessible label for screen readers (important for icon-only buttons) */
    ariaLabel?: string;
    /** Icon placement */
    iconPlacement?: 'start' | 'end';
    /** Full width */
    isFullWidth?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}

// Class output is semantic: `tio-btn tio-btn--{intent} [tio-btn--{appearance}]
// tio-btn--{size}`. All color / state / shape intent lives in the `.tio-btn`
// family in `styles.css` (the filled appearance is the unmodified base, so it
// needs no appearance modifier). See that block for the light-hex-preserved
// recipe and the dark-mode token routing.

const ICON_SIZES: Record<ButtonSize, number> = {
  sm: 16,
  md: 18,
  lg: 22,
};

/**
 * Button – A polished, zero-dependency button component.
 *
 * Replaces `@frontile/buttons` with an identical API surface plus:
 * - Animated three-dot loading indicator
 * - Subtle shadow and transition polish
 * - Full dark mode support
 *
 * @example Basic usage
 * ```gts
 * import Button from 'tio-ui/components/button';
 *
 * <Button @intent="primary">Save Changes</Button>
 * ```
 *
 * @example With icon
 * ```gts
 * <Button @intent="primary" @icon={{PhFloppyDisk}} @label="Save" />
 * ```
 *
 * @example Loading state
 * ```gts
 * <Button @intent="primary" @isRunning={{this.isSaving}}>
 *   Save
 * </Button>
 * ```
 */
export default class Button extends Component<ButtonSignature> {
  get intent(): ButtonIntent {
    return this.args.intent ?? 'default';
  }

  get appearance(): ButtonAppearance {
    return this.args.appearance ?? 'default';
  }

  get size(): ButtonSize {
    return this.args.size ?? 'md';
  }

  get isDisabled(): boolean {
    return this.args.isDisabled || this.args.isRunning || false;
  }

  get iconSize(): number {
    return this.args.iconSize ?? ICON_SIZES[this.size] ?? 18;
  }

  get buttonClasses(): string {
    const intent = `tio-btn--${this.intent}`;
    // The `default` (filled) appearance is the unmodified base — no modifier.
    const appearance = this.appearance === 'default' ? '' : `tio-btn--${this.appearance}`;
    const size = `tio-btn--${this.size}`;
    const width = this.args.isFullWidth ? 'tio-btn--full' : '';

    return ['tio-btn', intent, appearance, size, width].filter(Boolean).join(' ');
  }

  <template>
    <button
      type={{if @type @type "button"}}
      class={{this.buttonClasses}}
      disabled={{this.isDisabled}}
      aria-busy={{if @isRunning "true"}}
      aria-label={{@ariaLabel}}
      data-test-button
      data-test-hook={{@testHook}}
      data-test-button-intent={{this.intent}}
      data-test-button-appearance={{this.appearance}}
      data-test-button-size={{this.size}}
      ...attributes
    >
      {{! Loading overlay – three pulsing dots }}
      {{#if @isRunning}}
        <span class="tio-btn__loading" aria-label="Loading" role="status" data-test-button-loading>
          <span class="tio-btn__dot"></span>
          <span class="tio-btn__dot"></span>
          <span class="tio-btn__dot"></span>
        </span>
      {{/if}}

      {{! Content – fades out when loading }}
      <span
        data-test-button-content
        class="tio-btn__content {{if @isRunning 'tio-btn__content--hidden'}}"
      >
        {{#if @icon}}
          {{#if (eq @iconPlacement "end")}}
            {{#if @label}}
              <span>{{@label}}</span>
            {{else}}
              {{yield}}
            {{/if}}
            {{#let @icon as |Icon|}}
              <Icon @size={{this.iconSize}} />
            {{/let}}
          {{else}}
            {{#let @icon as |Icon|}}
              <Icon @size={{this.iconSize}} />
            {{/let}}
            {{#if @label}}
              <span>{{@label}}</span>
            {{else}}
              {{yield}}
            {{/if}}
          {{/if}}
        {{else if @label}}
          <span>{{@label}}</span>
        {{else}}
          {{yield}}
        {{/if}}
      </span>
    </button>
  </template>
}
